<template>
  <div class="form-responses-page">
    <div class="header">
      <div class="header-content">
        <button @click="$router.push('/form-builder')" class="btn-back">
          <v-icon small>mdi-arrow-left</v-icon> Back to Forms
        </button>
        <h1 class="title">{{ formTitle }}</h1>
      </div>
      <div class="header-actions">
        <button @click="exportCSV" class="btn-export" :disabled="responses.length === 0">
          <v-icon small>mdi-download</v-icon> Export CSV
        </button>
      </div>
    </div>

    <div class="content-container">
      <div v-if="loading" class="loading">
        <div class="loader"></div>
        <p>Loading responses...</p>
      </div>

      <div v-else-if="error" class="error-container">
        <div class="error-icon">⚠️</div>
        <h2>Error</h2>
        <p>{{ error }}</p>
      </div>

      <div v-else-if="responses.length === 0" class="empty-state">
        <div class="empty-icon">📝</div>
        <h2>No responses yet</h2>
        <p>Share your form to start collecting responses</p>
        <button @click="copyFormLink" class="btn-copy-link">
          <v-icon small>mdi-link</v-icon> Copy Form Link
        </button>
      </div>

      <div v-else class="responses-container">
        <div class="summary-card">
          <div class="summary-item">
            <div class="summary-number">{{ responses.length }}</div>
            <div class="summary-label">Total Responses</div>
          </div>
          <div class="summary-divider"></div>
          <div class="summary-item">
            <div class="summary-number">{{ formQuestions }}</div>
            <div class="summary-label">Questions</div>
          </div>
        </div>

        <div class="responses-list">
          <div v-for="(response, index) in responses" :key="response.id" class="response-card">
            <div class="response-header">
              <div class="response-number">Response #{{ responses.length - index }}</div>
              <div class="response-date">{{ formatDate(response.submittedAt) }}</div>
            </div>
            
            <div class="response-body">
              <div v-for="(value, key) in response.responses" :key="key" class="response-item">
                <div class="response-question">{{ key }}</div>
                <div class="response-answer">
                  <span v-if="Array.isArray(value)">{{ value.join(', ') || '—' }}</span>
                  <span v-else>{{ value || '—' }}</span>
                </div>
              </div>
            </div>

            <div class="response-footer">
              <button @click="exportSingleResponse(response)" class="btn-export-single">
                <v-icon small>mdi-download</v-icon> Export
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { db } from '../firebase'
import { collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore'

export default {
  name: 'FormResponsesPage',
  data() {
    return {
      formTitle: 'Form Responses',
      formQuestions: 0,
      responses: [],
      loading: true,
      error: null
    }
  },
  async mounted() {
    await this.loadResponses()
  },
  methods: {
    async loadResponses() {
      try {
        this.loading = true
        const formId = this.$route.params.id
        
        if (!formId) {
          this.error = 'No form ID provided'
          return
        }

        // Get form details
        const formDoc = await getDoc(doc(db, 'forms', formId))
        if (formDoc.exists()) {
          this.formTitle = formDoc.data().title || 'Form Responses'
          this.formQuestions = formDoc.data().questions ? formDoc.data().questions.length : 0
        }

        // Get responses
        const q = query(
          collection(db, 'formResponses'),
          where('formId', '==', formId),
          orderBy('submittedAt', 'desc')
        )
        
        const querySnapshot = await getDocs(q)
        
        this.responses = []
        querySnapshot.forEach((doc) => {
          this.responses.push({
            id: doc.id,
            ...doc.data()
          })
        })
      } catch (error) {
        console.error('Error loading responses:', error)
        this.error = 'Failed to load responses. Please try again.'
      } finally {
        this.loading = false
      }
    },
    formatDate(timestamp) {
      if (!timestamp) return 'Unknown date'
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    exportCSV() {
      if (this.responses.length === 0) return

      // Get all unique question keys
      const allKeys = new Set()
      this.responses.forEach(response => {
        Object.keys(response.responses).forEach(key => allKeys.add(key))
      })
      
      const headers = ['Submitted At', ...Array.from(allKeys)]
      const rows = this.responses.map(response => {
        const row = [this.formatDate(response.submittedAt)]
        allKeys.forEach(key => {
          const value = response.responses[key]
          if (Array.isArray(value)) {
            row.push(value.join('; '))
          } else {
            row.push(value || '')
          }
        })
        return row
      })

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `${this.formTitle.replace(/\s+/g, '_')}_responses.csv`
      link.click()
    },
    exportSingleResponse(response) {
      const data = {
        formTitle: response.formTitle,
        submittedAt: this.formatDate(response.submittedAt),
        responses: response.responses
      }

      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `response_${response.id}.json`
      link.click()
    },
    copyFormLink() {
      const formId = this.$route.params.id
      const link = `${window.location.origin}/form/${formId}`
      navigator.clipboard.writeText(link)
      alert('Form link copied to clipboard!')
    }
  }
}
</script>

<style scoped>
.form-responses-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding: 40px 20px;
}

.header {
  max-width: 1200px;
  margin: 0 auto 40px;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
}

.btn-back {
  padding: 10px 20px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.btn-back:hover {
  background: #f5f5f5;
  border-color: #673ab7;
  color: #673ab7;
}

.title {
  font-size: 32px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.btn-export {
  padding: 12px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s;
  box-shadow: 0 4px 15px rgba(103, 58, 183, 0.3);
}

.btn-export:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(103, 58, 183, 0.4);
}

.btn-export:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.content-container {
  max-width: 1200px;
  margin: 0 auto;
}

.loading,
.error-container,
.empty-state {
  background: white;
  padding: 80px 40px;
  border-radius: 12px;
  text-align: center;
}

.loader {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #673ab7;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-icon,
.empty-icon {
  font-size: 64px;
  margin-bottom: 20px;
}

.error-container h2,
.empty-state h2 {
  font-size: 24px;
  color: #1a1a1a;
  margin: 0 0 12px 0;
}

.error-container p,
.empty-state p {
  font-size: 16px;
  color: #666;
  margin: 0 0 24px 0;
}

.btn-copy-link {
  padding: 12px 32px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s;
}

.btn-copy-link:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(103, 58, 183, 0.4);
}

.summary-card {
  background: white;
  border-radius: 12px;
  padding: 30px;
  margin-bottom: 30px;
  display: flex;
  gap: 40px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.summary-item {
  flex: 1;
  text-align: center;
}

.summary-number {
  font-size: 48px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 8px;
}

.summary-label {
  font-size: 16px;
  color: #666;
  font-weight: 500;
}

.summary-divider {
  width: 1px;
  background: #e0e0e0;
}

.responses-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.response-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transition: all 0.3s;
}

.response-card:hover {
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
}

.response-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f0f0f0;
}

.response-number {
  font-size: 18px;
  font-weight: 700;
  color: #673ab7;
}

.response-date {
  font-size: 14px;
  color: #999;
}

.response-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
}

.response-item {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 16px;
}

.response-question {
  font-size: 14px;
  font-weight: 600;
  color: #666;
}

.response-answer {
  font-size: 15px;
  color: #1a1a1a;
  word-wrap: break-word;
}

.response-footer {
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
}

.btn-export-single {
  padding: 8px 16px;
  background: white;
  border: 1px solid #673ab7;
  border-radius: 6px;
  color: #673ab7;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.btn-export-single:hover {
  background: #f8f5fb;
}

@media (max-width: 768px) {
  .form-responses-page {
    padding: 20px 12px;
  }

  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }

  .title {
    font-size: 24px;
  }

  .summary-card {
    flex-direction: column;
    gap: 20px;
  }

  .summary-divider {
    width: 100%;
    height: 1px;
  }

  .response-item {
    grid-template-columns: 1fr;
    gap: 8px;
  }
}
</style>
